import { Mail, MapPin, Clock, Shield, Phone } from "lucide-react";
import NewsletterSignup from "@/components/NewsletterSignup";
import { EditableText as T } from "@/components/live/EditableText";
import { Section } from "@/components/live/Section";

const Contact = () => {
  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="page-header" data-testid="contact-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="contact.get-in-touch" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Get in Touch</T>
          <T k="contact.contact" as="h1" className="page-title animate-fade-in stagger-1">Contact</T>
          <T k="contact.reach-out-for" as="p" className="page-subtitle animate-fade-in stagger-2">Reach out for inquiries, questions, or to begin our introduction.</T>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-6 md:px-12 lg:px-24" data-testid="contact-methods">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Email */}
            <div className="glass rounded-xl p-8 card-hover" data-testid="contact-email">
              <div className="w-14 h-14 bg-jade-deep/10 rounded-full flex items-center justify-center mb-6">
                <Mail className="w-7 h-7 text-jade-text" />
              </div>
              <T k="contact.email" as="h3" className="font-heading text-xl text-charcoal mb-3">Email</T>
              <T k="contact.the-preferred-method" as="p" className="font-body text-stone-grey mb-4">The preferred method for all inquiries. I respond within 24-48 hours.</T>
              <T k="contact.bookkalaniluanagmailcom" as="a" 
                href="mailto:bookkalaniluana@gmail.com" 
                className="font-body text-jade-text hover:text-champagne-gold transition-colors">bookkalaniluana@gmail.com</T>
            </div>

            {/* Phone */}
            <div className="glass rounded-xl p-8 card-hover" data-testid="contact-phone">
              <div className="w-14 h-14 bg-jade-deep/10 rounded-full flex items-center justify-center mb-6">
                <Phone className="w-7 h-7 text-jade-text" />
              </div>
              <T k="contact.phone" as="h3" className="font-heading text-xl text-charcoal mb-3">Phone</T>
              <T k="contact.phone-text" as="p" className="font-body text-stone-grey mb-4">Text is the preferred method of phone contact.</T>
              <T k="contact.1-929-5515824" as="a" 
                href="tel:+19295515824" 
                className="font-body text-jade-text hover:text-champagne-gold transition-colors">+1 (929) 551-5824</T>
            </div>

            {/* Locations */}
            <div className="glass rounded-xl p-8 card-hover" data-testid="contact-locations">
              <div className="w-14 h-14 bg-jade-deep/10 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-jade-text" />
              </div>
              <T k="contact.locations" as="h3" className="font-heading text-xl text-charcoal mb-3">Locations</T>
              <T k="contact.based-in-the" as="p" className="font-body text-stone-grey mb-4">Based in the NYC/NJ metropolitan area with frequent visits to CA.</T>
              <div className="space-y-2">
                <T k="contact.new-york-new" as="p" className="font-body text-charcoal">• New York / New Jersey (Home)</T>
                <T k="contact.california-secondary" as="p" className="font-body text-charcoal">• California (Secondary)</T>
                <T k="contact.available-for-travel" as="p" className="font-body text-charcoal">• Available for Travel</T>
              </div>
            </div>
          </div>

          {/* Additional Info Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            {/* Response Time */}
            <div className="glass rounded-xl p-8 card-hover" data-testid="contact-response">
              <div className="w-14 h-14 bg-jade-deep/10 rounded-full flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-jade-text" />
              </div>
              <T k="contact.response-time" as="h3" className="font-heading text-xl text-charcoal mb-3">Response Time</T>
              <T k="contact.i-personally-review" as="p" multiline className="font-body text-stone-grey">I personally review each message and typically respond within 24-48 hours. Thoughtful, detailed inquiries receive priority attention.</T>
            </div>

            {/* Discretion */}
            <div className="glass rounded-xl p-8 card-hover" data-testid="contact-discretion">
              <div className="w-14 h-14 bg-jade-deep/10 rounded-full flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-jade-text" />
              </div>
              <T k="contact.discretion" as="h3" className="font-heading text-xl text-charcoal mb-3">Discretion</T>
              <T k="contact.your-privacy-is" as="p" className="font-body text-stone-grey">Your privacy is paramount. All communications are confidential and handled with the utmost care and professionalism.</T>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <Section k="contact.section.quick-links" label="quick links" className="py-16 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="quick-links">
        <div className="max-w-4xl mx-auto text-center">
          <T k="contact.before-you-reach" as="h2" className="font-heading text-3xl text-charcoal mb-8">Before You Reach Out</T>
          <div className="flex flex-wrap justify-center gap-4">
            <T k="contact.review-booking-etiquette" as="a" href="/booking-etiquette" className="btn-secondary">Review Booking Etiquette</T>
            <T k="contact.check-the-faq" as="a" href="/faq" className="btn-secondary">Check the FAQ</T>
            <T k="contact.submit-an-inquiry" as="a" href="/booking" className="btn-primary">Submit an Inquiry</T>
          </div>
        </div>
      </Section>

      {/* Social Links */}
      <Section k="contact.section.social-section" label="social" className="py-20 px-6 md:px-12 lg:px-24" data-testid="social-section">
        <div className="max-w-4xl mx-auto text-center">
          <T k="contact.follow-my-journey" as="h2" className="font-heading text-3xl text-charcoal mb-6">Follow My Journey</T>
          <T k="contact.stay-connected-through" as="p" className="font-body text-stone-grey mb-8 max-w-2xl mx-auto">Stay connected through my social platforms for travel updates, behind-the-scenes moments, and more.</T>
          <div className="flex justify-center gap-4">
            <a 
              href="https://x.com/KalaniLuana_3x" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-sand rounded-full flex items-center justify-center hover:bg-jade-deep hover:text-white transition-all"
              data-testid="social-twitter"
            >
              <span className="font-body text-lg">𝕏</span>
            </a>
            <a 
              href="https://www.instagram.com/Kalaniluana/" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-sand rounded-full flex items-center justify-center hover:bg-jade-deep hover:text-white transition-all"
              data-testid="social-instagram"
            >
              <T k="contact.ig" className="font-body text-lg">IG</T>
            </a>
            <a 
              href="https://www.tiktok.com/@kalaniluana" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-sand rounded-full flex items-center justify-center hover:bg-jade-deep hover:text-white transition-all"
              data-testid="social-tiktok"
            >
              <T k="contact.tt" className="font-body text-lg">TT</T>
            </a>
          </div>
        </div>
      </Section>

      {/* Newsletter */}
      <Section k="contact.section.contact-newsletter" label="contact newsletter" className="py-20 px-6 md:px-12 lg:px-24 bg-jade-deep" data-testid="contact-newsletter">
        <div className="max-w-2xl mx-auto text-center">
          <T k="contact.stay-connected" as="h2" className="font-heading text-3xl text-ivory mb-6">Stay Connected</T>
          <T k="contact.join-my-newsletter" as="p" className="font-body text-ivory/80 mb-8">Join my newsletter for exclusive updates on travel schedules and special announcements.</T>
          <div className="max-w-md mx-auto">
            <NewsletterSignup variant="dark" />
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
